import { Injectable, computed, signal } from '@angular/core';
import {
  loadStripe,
  Stripe,
  StripeCardElement,
  PaymentIntent,
  SetupIntent,
  StripeError,
  TokenResult,
  PaymentIntentResult,
  SetupIntentResult,
} from '@stripe/stripe-js';
import { Observable, from, map, catchError, of, throwError } from 'rxjs';
import { StripeBillingDetails } from '@chefami-monorepo/types';

/**
 * Centralized service for Stripe payment operations.
 *
 * Uses @stripe/stripe-js directly (no ngx-stripe dependency).
 * Supports dynamic key loading for multi-location scenarios.
 *
 * Three main use cases:
 * 1. Gift card purchase (one-time payment with payment intent)
 * 2. New subscription (payment intent OR setup intent based on charge_now flag)
 * 3. Payment method update (setup intent only)
 */
@Injectable({ providedIn: 'root' })
export class StripePaymentService {
  // Reactive state
  private readonly stripeInstanceSignal = signal<Stripe | null>(null);
  private readonly loadingSignal = signal(false);
  private readonly errorSignal = signal<string>('');

  // Public computed signals
  readonly stripeInstance = computed(() => this.stripeInstanceSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());
  readonly isInitialized = computed(() => this.stripeInstanceSignal() !== null);

  /**
   * Initialize Stripe with publishable key.
   * Can be called multiple times to switch between different Stripe accounts.
   */
  initializeStripe(publishableKey: string): Observable<Stripe> {
    if (!publishableKey) {
      this.errorSignal.set('Stripe publishable key is required');
      return throwError(() => new Error('Stripe publishable key is required'));
    }

    this.loadingSignal.set(true);
    this.errorSignal.set('');

    return from(loadStripe(publishableKey)).pipe(
      map((stripe) => {
        if (!stripe) {
          throw new Error('Failed to load Stripe');
        }
        this.stripeInstanceSignal.set(stripe);
        this.loadingSignal.set(false);
        return stripe;
      }),
      catchError((error) => {
        this.loadingSignal.set(false);
        const errorMessage = error?.message || 'Failed to initialize Stripe';
        this.errorSignal.set(errorMessage);
        throw error;
      }),
    );
  }

  /**
   * Create Stripe token from card element.
   */
  createToken(
    cardElement: StripeCardElement,
    billingDetails: StripeBillingDetails,
  ): Observable<TokenResult> {
    const stripe = this.stripeInstanceSignal();
    if (!stripe) {
      this.errorSignal.set(
        'Stripe not initialized. Call initializeStripe() first.',
      );
      const error: StripeError = {
        type: 'validation_error',
        message: 'Stripe not initialized',
      };
      return of({ error });
    }

    this.loadingSignal.set(true);
    this.errorSignal.set('');

    return from(
      stripe.createToken(cardElement, {
        name: billingDetails.name || billingDetails.email || '',
        address_line1: billingDetails.address?.line1,
        address_line2: billingDetails.address?.line2,
        address_city: billingDetails.address?.city,
        address_state: billingDetails.address?.state,
        address_zip: billingDetails.address?.postal_code,
        address_country: billingDetails.address?.country,
      }),
    ).pipe(
      map((result) => {
        this.loadingSignal.set(false);
        if (result.error) {
          this.errorSignal.set(result.error.message || 'An error occurred');
        }
        return result;
      }),
      catchError((error) => {
        this.loadingSignal.set(false);
        const errorMessage = error?.message || 'Failed to create token';
        this.errorSignal.set(errorMessage);
        const stripeError: StripeError = {
          type: 'api_error',
          message: errorMessage,
        };
        return of({ error: stripeError });
      }),
    );
  }

  /**
   * Confirm card payment with payment intent (for immediate charges).
   * Used for gift card purchases and subscriptions when charge_now = true.
   */
  confirmCardPayment(
    clientSecret: string,
    cardElement: StripeCardElement,
    billingDetails: StripeBillingDetails,
  ): Observable<PaymentIntentResult> {
    const stripe = this.stripeInstanceSignal();
    if (!stripe) {
      this.errorSignal.set(
        'Stripe not initialized. Call initializeStripe() first.',
      );
      const error: StripeError = {
        type: 'validation_error',
        message: 'Stripe not initialized',
      };
      return of({ error });
    }

    this.loadingSignal.set(true);
    this.errorSignal.set('');

    return from(
      stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: billingDetails,
        },
      }),
    ).pipe(
      map((result) => {
        this.loadingSignal.set(false);
        if (result.error) {
          this.errorSignal.set(
            result.error.message || 'Payment confirmation failed',
          );
        }
        return result;
      }),
      catchError((error) => {
        this.loadingSignal.set(false);
        const errorMessage = error?.message || 'Payment confirmation failed';
        this.errorSignal.set(errorMessage);
        const stripeError: StripeError = {
          type: 'api_error',
          message: errorMessage,
        };
        return of({ error: stripeError });
      }),
    );
  }

  /**
   * Confirm card setup with setup intent (for saving payment method).
   * Used for subscriptions when charge_now = false and payment method updates.
   */
  confirmCardSetup(
    clientSecret: string,
    cardElement: StripeCardElement,
    billingDetails: StripeBillingDetails,
  ): Observable<SetupIntentResult> {
    const stripe = this.stripeInstanceSignal();
    if (!stripe) {
      this.errorSignal.set(
        'Stripe not initialized. Call initializeStripe() first.',
      );
      const error: StripeError = {
        type: 'validation_error',
        message: 'Stripe not initialized',
      };
      return of({ error });
    }

    this.loadingSignal.set(true);
    this.errorSignal.set('');

    return from(
      stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: billingDetails,
        },
      }),
    ).pipe(
      map((result) => {
        this.loadingSignal.set(false);
        if (result.error) {
          this.errorSignal.set(
            result.error.message || 'Setup confirmation failed',
          );
        }
        return result;
      }),
      catchError((error) => {
        this.loadingSignal.set(false);
        const errorMessage = error?.message || 'Setup confirmation failed';
        this.errorSignal.set(errorMessage);
        const stripeError: StripeError = {
          type: 'api_error',
          message: errorMessage,
        };
        return of({ error: stripeError });
      }),
    );
  }

  /**
   * Check if intent status indicates success
   *
   * @param status - Stripe intent status
   * @returns True if succeeded, false otherwise
   */
  isIntentSuccessful(
    status: PaymentIntent['status'] | SetupIntent['status'],
  ): boolean {
    return status === 'succeeded';
  }

  /**
   * Check if intent status requires user action (3D Secure)
   *
   * @param status - Stripe intent status
   * @returns True if action required, false otherwise
   */
  isIntentActionRequired(
    status: PaymentIntent['status'] | SetupIntent['status'],
  ): boolean {
    return status === 'requires_action';
  }

  /**
   * Check if intent status indicates processing
   *
   * @param status - Stripe intent status
   * @returns True if processing, false otherwise
   */
  isIntentProcessing(
    status: PaymentIntent['status'] | SetupIntent['status'],
  ): boolean {
    return status === 'processing';
  }

  /**
   * Check if intent status indicates failure
   *
   * @param status - Stripe intent status
   * @returns True if failed, false otherwise
   */
  isIntentFailed(
    status: PaymentIntent['status'] | SetupIntent['status'],
  ): boolean {
    return status === 'requires_payment_method' || status === 'canceled';
  }

  /**
   * Get user-friendly error message for intent status
   *
   * @param status - Stripe intent status
   * @returns User-friendly error message
   */
  getIntentStatusMessage(
    status: PaymentIntent['status'] | SetupIntent['status'],
  ): string {
    switch (status) {
      case 'succeeded':
        return 'Payment successful';
      case 'processing':
        return 'Your payment is being processed. Please wait...';
      case 'requires_payment_method':
        return 'Payment failed. Please try again with a different payment method.';
      case 'requires_action':
        return 'Additional authentication required';
      case 'canceled':
        return 'Payment was canceled';
      default:
        return 'An error occurred. Please try again.';
    }
  }

  /**
   * Reset error state
   */
  clearError(): void {
    this.errorSignal.set('');
  }

  /**
   * Reset service state
   */
  reset(): void {
    this.stripeInstanceSignal.set(null);
    this.loadingSignal.set(false);
    this.errorSignal.set('');
  }
}
